// src/toolkitServices/employeeApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const employeeApi = createApi({
  reducerPath: "employeeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL, // e.g. http://localhost:4000/api
    credentials: "include", // ✅ includes cookies for httpOnly token
    prepareHeaders: (headers) => {
      // 🔒 If you switch to JWT header auth later, uncomment below lines
      // const token = localStorage.getItem("token");
      // if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["Employee"],
  endpoints: (builder) => ({
    /* 🟩 CREATE EMPLOYEE */
    createEmployee: builder.mutation({
      query: (body) => ({
        url: "/employees/create",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Employee"],
    }),

    /* 🟩 LIST / FILTER EMPLOYEES */
    getAllEmployees: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        department,
        branch,
        active,
      } = {}) => {
        const params = { page, limit, q: search };

        if (department) params.department = department?.value || department;
        if (branch) params.branch = branch?.value || branch; // ✅ MUST be ObjectId string
        if (typeof active === "boolean") params.active = active;

        return { url: "/employees", params };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((emp) => ({ type: "Employee", id: emp._id })),
              { type: "Employee" },
            ]
          : [{ type: "Employee" }],
      keepUnusedDataFor: 30,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }),
     getAllEmployeesAll: builder.query({
      query: ({
        page = 1,
        limit = 200,
        search = "",
        active,
        role,
        department,
        branch,
        counter,
      } = {}) => {
        const params = { page, limit, q: search };
        if (typeof active === "boolean") params.active = active;
        if (role) params.role = role;
        if (department) params.department = department;
        if (branch) params.branch = branch;
        if (counter) params.counter = counter;
        return { url: "/employees/all", params };
      },
      providesTags: ["Employee"],
    }),

    /* 🟩 GET EMPLOYEE BY ID */
    getEmployeeById: builder.query({
      query: (id) => `/employees/${id}`,
      providesTags: (_res, _err, id) => [{ type: "Employee", id }],
    }),

    /* 🟩 UPDATE EMPLOYEE */
    updateEmployee: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/employees/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Employee", id },
        { type: "Employee" },
      ],
    }),
    

    /* 🟩 DELETE EMPLOYEE */
    deleteEmployee: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Employee"],
    }),

    /* 🟩 ACTIVATE EMPLOYEE */
    activateEmployee: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}/activate`,
        method: "PATCH",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Employee", id },
        { type: "Employee" },
      ],
    }),

    /* 🟩 DEACTIVATE EMPLOYEE */
    deactivateEmployee: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}/deactivate`,
        method: "PATCH",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Employee", id },
        { type: "Employee" },
      ],
    }),

    /* 🟩 REGENERATE PASSWORD */
    regenerateEmployeePassword: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}/regenerate-password`,
        method: "PATCH",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Employee", id },
        { type: "Employee" },
      ],
    }),

    /* 🟩 (Optional) CREATE LOGIN FOR EXISTING EMPLOYEE */
    createEmployeeLogin: builder.mutation({
      query: ({ id, email, password }) => ({
        url: `/employees/${id}`,
        method: "PUT",
        body: { createLogin: true, email, password },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Employee", id },
        { type: "Employee" },
      ],
    }),

    /* 🟩 WITHDRAW EMPLOYEE COMMISSION */
    withdrawEmployeeCommission: builder.mutation({
      query: ({ id, amount, full = false, reference = "", notes = "" }) => ({
        url: `/employees/${id}/commission/withdraw`,
        method: "POST",
        body: { amount, full, reference, notes },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Employee", id },
        { type: "Employee" },
      ],
    }),

     requestEmployeeCommission: builder.mutation({
      query: ({ id, amount }) => ({
        url: `/employees/${id}/commission/redeem-request`,
        method: "POST",
        body: { amount },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Employee", id },
        { type: "Employee" },
      ],
    }),
        /* 🟩 GET EMPLOYEE COMMISSION LEDGER (employee-wise) */
    getEmployeeCommissionLedger: builder.query({
      query: ({
        id,
        page = 1,
        limit = 20,
        type,
        from,
        to,
        q = "",
      } = {}) => {
        const params = { page, limit };

        if (q) params.q = q;
        if (type) params.type = type;
        if (from) params.from = from; // "YYYY-MM-DD"
        if (to) params.to = to;       // "YYYY-MM-DD"

        return { url: `/employees/${id}/commission/ledger`, params };
      },
      providesTags: (_res, _err, arg) => [
        { type: "Employee", id: arg?.id },
        { type: "Employee" },
      ],
      keepUnusedDataFor: 30,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }),

    /* 🟩 LIST COMMISSION LEDGERS (tenant-wide / all employees) */
    listCommissionLedgers: builder.query({
      query: ({
        page = 1,
        limit = 20,
        employeeId,
        type,
        from,
        to,
        q = "",
      } = {}) => {
        const params = { page, limit };

        if (q) params.q = q;
        if (employeeId) params.employeeId = employeeId;
        if (type) params.type = type;
        if (from) params.from = from;
        if (to) params.to = to;

        return { url: `/employees/commission/ledger`, params };
      },
      providesTags: [{ type: "Employee" }],
      keepUnusedDataFor: 30,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }),

  }),
});

/* 🧩 Export Hooks */
export const {
  useCreateEmployeeMutation,
  useGetAllEmployeesQuery,
  useGetAllEmployeesAllQuery,
  useGetEmployeeByIdQuery,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useActivateEmployeeMutation,
  useDeactivateEmployeeMutation,
  useRegenerateEmployeePasswordMutation,
  useCreateEmployeeLoginMutation, // optional hook for enabling login
  useWithdrawEmployeeCommissionMutation, // ✅ ADD
  useRequestEmployeeCommissionMutation, // ✅ ADD
    useGetEmployeeCommissionLedgerQuery,
  useListCommissionLedgersQuery,
} = employeeApi;

export default employeeApi;
